/**
 * Service with REST request functions to hit the rails services.
 *
 * @todo we should be able to reorganize this / consolidate some code.
 */
import { Noticeable } from '@honeybadger-io/core/build/src/types';
import { createAxiosClient } from 'src/utils/axiosClient';
import logger from 'logger';
import { getNextConfigValues } from 'next-config';
import { isServerSide } from 'src/utils/next';

import { ApiErrorBase, ApiResponseBase } from './interfaces';
import { AxiosError } from 'axios';

const {
  publicRuntimeConfig: { apiUrls },
  serverRuntimeConfig: { apiUrls: serverSideApiUrls },
} = getNextConfigValues();

interface BraintreeTokenResponse extends ApiResponseBase {
  data: {
    token: string;
  };
  error: ApiErrorBase;
}

export interface PaymentMethod {
  default: boolean;
  expirationMonth: string;
  expirationYear: string;
  id: number;
  lastFour: string;
  preAuthValid: boolean;
  type: string;
  additionalInfo?: any;
}

export interface AddPaymentMethodResponse extends ApiResponseBase {
  data: PaymentMethod;
  error: ApiErrorBase;
}
const generalClient = createAxiosClient({
  baseURL: apiUrls.railsRoot,
  withCredentials: true,
});

// Client side requests need to make requests using relative URLs, while server side requests
// need the absolute URL.

// @todo we should make a HOF to wrap these individual request functions in that can
// use the appropriate client to make the request.
const clientSideCheckoutClient = createAxiosClient({
  baseURL: apiUrls.railsCheckoutV1,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * Construct and return a KY instance for server-side usage against the checkout V1 route.
 *
 * Doing like this ensures that we don't try to create a server side instance on the client side
 * which crashes the client.
 */
const getServerSideCheckoutClient = () =>
  createAxiosClient({
    baseURL: `${serverSideApiUrls.railsRest}/checkout/v1`,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

export const getBraintreeToken = async (
  token: string,
): Promise<BraintreeTokenResponse['data']> => {
  try {
    const { data } = await clientSideCheckoutClient.get<BraintreeTokenResponse>(
      'payment_methods/new',
      {
        params: {
          recaptcha_token: token,
        },
      },
    );
    return data.data;
  } catch (e: any) {
    logger.error({
      msg: 'Failed to get braintree token from backend',
      noticable: e,
    });
    return e.response && e.response.data;
  }
};

export const getAnonymousBraintreeToken = async (
  recaptchaToken: string,
): Promise<BraintreeTokenResponse['data']> => {
  try {
    const { data } = await clientSideCheckoutClient.get<BraintreeTokenResponse>(
      'payment_methods/token',
      {
        params: {
          recaptcha_token: recaptchaToken,
        },
      },
    );
    return data.data;
  } catch (e: any) {
    logger.error({
      msg: 'Failed to get anonymous braintree token from backend',
      noticable: e,
    });
    return e.response && e.response.data;
  }
};

export const addBraintreePaymentMethod = async ({
  nonce,
  isDefault,
  loggingContext,
}: {
  nonce: string;
  isDefault: boolean;
  loggingContext?: Record<string, unknown>;
}): Promise<AddPaymentMethodResponse['data'] | null> => {
  try {
    const { data } =
      await clientSideCheckoutClient.post<AddPaymentMethodResponse>(
        'payment_methods',
        {
          nonce,
          is_default: isDefault,
        },
      );

    // response.error is returned on a successful response but has a null message
    //, so only log if there's an error message present, and not only based on response.error.
    if (data?.error?.message) {
      logger.error({
        msg:
          data?.error?.message ||
          'Failed to create Braintree payment method. Check Rails logs for details.',
        extra: {
          context: {
            ...loggingContext,
            err: data.error,
            info: 'This is often caused by a Braintree payment method validation failure.',
          },
        },
      });
    }

    return data?.data;
  } catch (e: unknown) {
    if (e instanceof AxiosError && e.response?.status === 422) {
      // Rails returns a 422 when there's a payment method validation failure. We can't do anything about these failures, so this only warrants warn-level logging.
      logger.warn({
        msg: 'Failed to create Braintree payment method due to Braintree payment method validation failure.',
        extra: {
          context: loggingContext,
        },
      });
    } else {
      const err = e as GetPaymentMethodsResponse;
      logger.error({
        msg:
          err?.error?.message ||
          'Failed to create Braintree payment method due to unexpected error. Check Rails logs for details.',
        noticable: e as Noticeable,
        extra: { context: { ...loggingContext, err: err?.error } },
      });
    }

    return null;
  }
};

interface GetPaymentMethodsResponse extends ApiResponseBase {
  data: PaymentMethod[];
  error: ApiErrorBase;
}

export const getBraintreePaymentMethods = async (): Promise<
  PaymentMethod[]
> => {
  try {
    const { data } =
      await clientSideCheckoutClient.get<GetPaymentMethodsResponse>(
        'payment_methods',
      );
    return data.data;
  } catch (e: any) {
    logger.error({
      msg: 'Failed to get existing payment methods',
      noticable: e,
    });
    return e.response && e.response.data;
  }
};

export interface MetroInfo {
  additionalDeliveryFeeCents: number;
  allowedZips: string[];
  deliveryDates: string[]; // entry format === '2022-03-06'
  deliveryFeeCents: number;
  id: number;
  metroStates: string[]; // 2-letter state abbreviations 'PA'
  name: string;
  pickupAvailable: boolean;
  prices: unknown;
  regionalShipping: boolean;
  sustainableZips: string[];
  validMealQuantities: number[];
}

interface GetMetroInfoResponse extends ApiResponseBase {
  data: MetroInfo;
  error: ApiErrorBase;
}

/**
 * Get additional info for a metro based off of a query zip
 */
export const getMetroInfo = async (
  metroZip: string,
  tenantId: number,
): Promise<MetroInfo> => {
  const url = `metros?zipcode=${metroZip}&tenant_id=${tenantId}`;
  const res = clientSideCheckoutClient.get<GetMetroInfoResponse>(url);
  const { data } = await res;
  return data.data;
};

interface PostProspectPayload {
  tenant_id: number;
  email: string;
  name: string;
  source: string;
  zip_code: string;
}

/**
 * Post prospects
 *
 * Don't need to do anything with the return, so just catch error and log when using.
 */
export const postProspect = async (
  payload: PostProspectPayload,
): Promise<unknown> => {
  const url = 'prospects';
  return await clientSideCheckoutClient.post(
    url,
    {
      ...payload,
    },
    { withCredentials: true },
  );
};

// note -- not exhaustive; minimum fields needed at time of writing.
interface FetchedMetroForZip {
  id: number;
  regionalShipping: boolean;
  customizeBy: string;
  deliveryWeek: string;
  deliveryDates: string[];
  estimatedTransitTime: number;
}

export const getMetroForZip = async ({
  zipcode,
  tenantId,
}: {
  zipcode: string;
  tenantId: number;
}): Promise<FetchedMetroForZip> => {
  try {
    const client = isServerSide()
      ? getServerSideCheckoutClient()
      : clientSideCheckoutClient;

    // A very strange timeout bug pops up with ZIP codes that begin with "0" when we use
    // `await client.get(args).json()` here, so that's why we're breaking that up into
    // two statements below.
    const response = await client.get<{
      data: FetchedMetroForZip;
    }>('metros', {
      params: {
        zipcode: zipcode,
        tenant_id: tenantId,
        // with this set to true we won't get back list of states, which is good b/c we don't need them, so more performant.
        is_catalog: 'true',
      },
    });
    const { data } = response;
    return data.data;
  } catch (e) {
    logger.error({
      msg: 'Failed to fetch metro for tenant zipcode',
      noticable: e as Noticeable,
      extra: {
        context: {
          zipcode,
          tenantId,
        },
      },
    });
    throw e;
  }
};

export type DiscountCode = {
  code: string;
  publicDescription?: string;
  totalMealMinimum?: number;
  eligibleForEcomm: boolean;
  eligibleForPrograms: boolean;
};

export const getDiscountCodeServerSide = async ({
  discountCode,
  tenantId,
}: {
  discountCode: string;
  tenantId: number;
  eligibleForEcomm?: boolean;
  eligibleForPrograms?: boolean;
}): Promise<DiscountCode> => {
  const client = createAxiosClient({
    baseURL: `${serverSideApiUrls.railsRest}`,
  });

  const response = await client.get<{
    data: {
      code: string;
      publicDescription?: string;
      totalMealMinimum?: number;
      eligibleForEcomm: boolean;
      eligibleForPrograms: boolean;
    };
  }>('/legacy-rails/discount', {
    params: {
      code: discountCode,
      tenant_id: tenantId,
    },
  });

  const {
    data: { data },
  } = response;

  return {
    code: data.code,
    publicDescription: data.publicDescription,
    totalMealMinimum: data.totalMealMinimum,
    eligibleForEcomm: data.eligibleForEcomm,
    eligibleForPrograms: data.eligibleForPrograms,
  };
};

export const getDiscountCodeClientSide = async ({
  discountCode,
  tenantId,
}: {
  discountCode: string;
  tenantId: number;
}): Promise<DiscountCode> => {
  const response = await generalClient.get<{
    data: {
      code: string;
      publicDescription?: string;
      totalMealMinimum?: number;
      eligibleForEcomm: boolean;
      eligibleForPrograms: boolean;
    };
  }>('legacy-rails/discount', {
    params: {
      code: discountCode,
      tenant_id: tenantId,
      eligible_for_ecomm: true,
    },
  });

  const {
    data: { data },
  } = response;

  return {
    code: data.code,
    publicDescription: data.publicDescription,
    totalMealMinimum: data.totalMealMinimum,
    eligibleForEcomm: data.eligibleForEcomm,
    eligibleForPrograms: data.eligibleForPrograms,
  };
};
